/**
 * WorksheetTags — tema + activity + age + duration pills under the hero.
 *
 * Per doc 07 §3: "tags (tema + activity + age + duration)"
 *
 * - Tema pills: from worksheet.temaNames[] (max 3), brand-purple tint
 * - Activity pill: green tint, emoji prefix
 * - Age pill: orange tint (from age_label or age_range)
 * - Duration pill: gray tint, ⏱ prefix
 *
 * All pills wrap (flexWrap) so they reflow on small screens.
 */

import { StyleSheet, Text, View } from 'react-native';

import { Brand, Spacing } from '@/constants/theme';
import { activityIcon } from '@/lib/icons';
import { AppIcon } from '@/components/app-icon';
import type { WorksheetDetail } from '@/lib/api/worksheet';

interface WorksheetTagsProps {
  worksheet: WorksheetDetail;
}

export function WorksheetTags({ worksheet }: WorksheetTagsProps) {
  const temas = worksheet.temaNames.slice(0, 3);
  const activity = worksheet.activity_label;
  const age = worksheet.age_label ?? worksheet.age_range;
  const duration = worksheet.duration_minutes;

  return (
    <View style={styles.container}>
      {temas.map((tema) => (
        <View key={`tema-${tema}`} style={[styles.pill, styles.pillTema]}>
          <Text style={[styles.pillText, styles.pillTextTema]}>{tema}</Text>
        </View>
      ))}
      {activity ? (
        <View style={[styles.pill, styles.pillActivity]}>
          <AppIcon source={activityIcon(activity)} size={16} />
          <Text style={[styles.pillText, styles.pillTextActivity]}>{activity}</Text>
        </View>
      ) : null}
      {age ? (
        <View style={[styles.pill, styles.pillAge]}>
          <Text style={[styles.pillText, styles.pillTextAge]}>{age}</Text>
        </View>
      ) : null}
      {duration ? (
        <View style={[styles.pill, styles.pillDuration]}>
          <Text style={[styles.pillText, styles.pillTextDuration]}>{duration} menit</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.two,
    paddingHorizontal: Spacing.four,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
  pillTema: {
    backgroundColor: 'rgba(86, 63, 122, 0.10)',
    borderWidth: 1,
    borderColor: Brand.primary,
  },
  pillActivity: {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    borderWidth: 1,
    borderColor: '#4caf50',
  },
  pillAge: {
    backgroundColor: 'rgba(255, 138, 61, 0.12)',
    borderWidth: 1,
    borderColor: '#ff8a3d',
  },
  pillDuration: {
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#9CA3AF',
  },
  pillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  pillTextTema: { color: Brand.primary },
  pillTextActivity: { color: '#15803D' },
  pillTextAge: { color: '#c2410c' },
  pillTextDuration: { color: '#4b5563' },
});
